import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Scale, ClipboardList, Search, MapPin, Phone, MessageSquare, Mail,
  Globe, Languages, Info, ArrowLeft, ArrowRight, Building2, AlertTriangle,
  Calendar, ScrollText,
} from "lucide-react";
import api from "@/lib/api";

/**
 * /directory — public directory of Immigration Attorneys and Paralegal
 * "Form-Filler Assistants".
 *
 * IMPORTANT product rule:
 *  - Contact info is displayed as plain text — NO mailto:/tel:/sms: links,
 *    no hyperlinks anywhere on an assistant's card, no click-throughs.
 *  - DS-260USA.com does NOT endorse anyone in this directory.
 *  - Listings are FREE; nobody pays to appear here.
 */

const US_STATES = [
  ["", "All states"], ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"],
  ["AR", "Arkansas"], ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"],
  ["DE", "Delaware"], ["DC", "D.C."], ["FL", "Florida"], ["GA", "Georgia"],
  ["HI", "Hawaii"], ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"],
  ["IA", "Iowa"], ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"],
  ["ME", "Maine"], ["MD", "Maryland"], ["MA", "Massachusetts"], ["MI", "Michigan"],
  ["MN", "Minnesota"], ["MS", "Mississippi"], ["MO", "Missouri"], ["MT", "Montana"],
  ["NE", "Nebraska"], ["NV", "Nevada"], ["NH", "New Hampshire"], ["NJ", "New Jersey"],
  ["NM", "New Mexico"], ["NY", "New York"], ["NC", "North Carolina"], ["ND", "North Dakota"],
  ["OH", "Ohio"], ["OK", "Oklahoma"], ["OR", "Oregon"], ["PA", "Pennsylvania"],
  ["PR", "Puerto Rico"], ["RI", "Rhode Island"], ["SC", "South Carolina"], ["SD", "South Dakota"],
  ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"], ["VT", "Vermont"],
  ["VA", "Virginia"], ["WA", "Washington"], ["WV", "West Virginia"],
  ["WI", "Wisconsin"], ["WY", "Wyoming"],
];

export default function DirectoryPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stateFilter, setStateFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (stateFilter) params.state = stateFilter;
      const { data } = await api.get("/directory", { params });
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, [stateFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const ql = q.toLowerCase();
    return rows.filter((r) => [
      r.full_name, r.firm_name, r.city, r.state, r.bar_state, r.bar_number,
      ...(r.languages || []), ...(r.services_offered || []),
    ].join(" ").toLowerCase().includes(ql));
  }, [rows, q]);

  const attorneys = filtered.filter((r) => r.type === "attorney");
  const paralegals = filtered.filter((r) => r.type === "paralegal");

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-app)" }}>
      <DirectoryTopBar />
      <div className="mx-auto max-w-6xl px-6 py-10" data-testid="directory-page">
        <div className="mb-6">
          <div className="pill pill-ai mb-3 inline-flex">Directory</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold mb-3" style={{ color: "var(--text-primary)" }}>
            Immigration Attorneys &amp; Form-Filler Assistants
          </h1>
          <p className="text-[15px] leading-relaxed max-w-3xl" style={{ color: "var(--text-secondary)" }}>
            Assistants who have asked to be listed here. Browse their credentials, then contact them
            directly using the plain-text details on their card.
          </p>
        </div>

        <NonEndorsementNotice />

        {/* Search / filter */}
        <div className="card-surface p-4 mb-5 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2"
                    style={{ color: "var(--text-secondary)" }} />
            <input
              type="text"
              className="field-input pl-9"
              placeholder="Search by name, firm, city, language…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="directory-search"
            />
          </div>
          <select
            className="field-input"
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            data-testid="directory-state-filter"
            style={{ maxWidth: 200 }}
          >
            {US_STATES.map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
          <Link to="/directory/apply" className="btn-ghost text-sm ml-auto" data-testid="directory-apply-link">
            Are you an assistant? Apply to be listed →
          </Link>
        </div>

        {loading ? (
          <div className="p-8 text-sm" style={{ color: "var(--text-secondary)" }}>Loading directory…</div>
        ) : (
          <>
            <DirectorySection
              testId="directory-attorneys"
              title="Licensed Immigration Attorneys"
              subtitle="Each attorney lists their state bar and bar number so you can verify their license independently."
              icon={Scale}
              items={attorneys}
              renderCard={(a) => <AttorneyCard a={a} key={a.id} />}
            />
            <DirectorySection
              testId="directory-paralegals"
              title="Form Filler Assistants"
              subtitle={
                <>
                  <span className="block mb-2">Non-attorneys who help petitioners organize and complete USCIS paperwork.</span>
                  <span className="block p-2 rounded border-l-4 text-xs" style={{
                    background: "var(--warn-bg)", borderColor: "var(--warn-border)",
                    color: "var(--warn-text)"
                  }} data-testid="directory-assistant-disclaimer">
                    <strong>Legal-advice disclaimer</strong> — Form Filler Assistants help you complete your
                    forms. They do not provide legal advice. For legal questions, engage a licensed
                    immigration attorney.
                  </span>
                </>
              }
              icon={ClipboardList}
              items={paralegals}
              renderCard={(p) => <ParalegalCard p={p} key={p.id} />}
            />
          </>
        )}

        <FooterDisclaimer />
      </div>
    </div>
  );
}

/* ---------- Top bar ---------- */

function DirectoryTopBar() {
  return (
    <header
      className="sticky top-0 z-30 backdrop-blur-md border-b"
      style={{ borderColor: "var(--border-stone)", background: "rgba(249,248,245,0.90)" }}
    >
      <div className="mx-auto max-w-7xl px-6 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="directory-logo">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center"
               style={{ background: "var(--action)" }}>
            <ScrollText className="w-4 h-4 text-white" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-[16px]" style={{ color: "var(--text-primary)" }}>
              DS-260USA<span style={{ color: "var(--action)" }}>.com</span>
            </div>
            <div className="text-[10.5px] uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
              Directory of Assistants
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/" className="btn-ghost text-sm flex items-center gap-1"
                data-testid="directory-back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </Link>
        </div>
      </div>
    </header>
  );
}

/* ---------- Non-endorsement banner ---------- */

function NonEndorsementNotice() {
  return (
    <div
      className="mb-5 rounded-lg p-4 flex items-start gap-3"
      style={{ background: "var(--warn-bg)", border: "1px solid var(--warn-border)", color: "var(--warn-text)" }}
      data-testid="directory-non-endorsement"
    >
      <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
      <div className="text-[13px] leading-relaxed">
        <div className="font-bold mb-1">DS-260USA.com does NOT endorse or recommend anyone in this directory.</div>
        <div>
          Listings are provided as a <strong>convenience only</strong>. We have not vetted the
          individuals or firms listed here, we receive <strong>no payment</strong> for these
          listings, and we have <strong>no association</strong> with them. It is your
          responsibility to independently verify any license, credential, or claim before
          engaging an assistant. Attorney bar admissions can be verified with the state bar
          referenced on each attorney&apos;s card.
        </div>
      </div>
    </div>
  );
}

/* ---------- Section wrapper ---------- */

function DirectorySection({ testId, title, subtitle, icon: Icon, items, renderCard }) {
  return (
    <section className="mb-10" data-testid={testId}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center"
             style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-border)" }}>
          <Icon className="w-4 h-4" style={{ color: "var(--ai-text)" }} />
        </div>
        <div>
          <h2 className="font-display text-xl font-extrabold" style={{ color: "var(--text-primary)" }}>{title}</h2>
          <p className="text-xs" style={{ color: "var(--text-secondary)" }}>{subtitle}</p>
        </div>
        <span className="ml-auto text-xs pill" style={{ background: "#F1EEE7", color: "var(--text-secondary)" }}>
          {items.length} listed
        </span>
      </div>
      {items.length === 0 ? (
        <div className="card-surface p-6 text-center text-sm" style={{ color: "var(--text-secondary)" }}>
          No {title.toLowerCase()} match your search yet.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">{items.map(renderCard)}</div>
      )}
    </section>
  );
}

/* ---------- Cards ----------
   Contact info rendered as plain text (span/div), never anchors — per product spec. */

function AttorneyCard({ a }) {
  return (
    <div className="card-surface p-5" data-testid={`attorney-${a.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="pill" style={{ background: "#EEF2FF", color: "#3730A3" }}>
            <Scale className="w-3 h-3" /> Attorney
          </div>
          <h3 className="font-display font-bold text-lg mt-2 leading-tight" style={{ color: "var(--text-primary)" }}>
            {a.full_name}
          </h3>
          {a.firm_name && (
            <div className="text-sm flex items-center gap-1.5 mt-0.5" style={{ color: "var(--text-secondary)" }}>
              <Building2 className="w-3.5 h-3.5" />{a.firm_name}
            </div>
          )}
        </div>
      </div>

      {/* Bar credentials — required for attorneys */}
      <div
        className="rounded-md p-3 mt-3 text-xs"
        style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-border)", color: "var(--ai-text)" }}
      >
        <div className="uppercase tracking-wider font-semibold text-[10.5px] mb-1">Bar credentials</div>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <PlainField label="State" value={a.bar_state} />
          <PlainField label="Bar #" value={a.bar_number} />
          <PlainField label="Admitted" value={formatDate(a.bar_admission_date)} icon={Calendar} />
        </div>
      </div>

      <ContactBlock item={a} />
      {a.bio && <BioBlock text={a.bio} />}
    </div>
  );
}

function ParalegalCard({ p }) {
  return (
    <div className="card-surface p-5" data-testid={`paralegal-${p.id}`}>
      <div className="pill" style={{ background: "#F0FDF4", color: "#166534" }}>
        <ClipboardList className="w-3 h-3" /> Paralegal / Form Filler
      </div>
      <h3 className="font-display font-bold text-lg mt-2 leading-tight" style={{ color: "var(--text-primary)" }}>
        {p.full_name}
      </h3>
      {p.firm_name && (
        <div className="text-sm flex items-center gap-1.5 mt-0.5" style={{ color: "var(--text-secondary)" }}>
          <Building2 className="w-3.5 h-3.5" />{p.firm_name}
        </div>
      )}
      {p.years_experience != null && (
        <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
          {p.years_experience} year{p.years_experience === 1 ? "" : "s"} of experience
        </div>
      )}
      {p.services_offered && p.services_offered.length > 0 && (
        <div className="mt-3">
          <div className="uppercase tracking-wider font-semibold text-[10.5px] mb-1"
               style={{ color: "var(--text-secondary)" }}>Services offered</div>
          <div className="flex flex-wrap gap-1.5">
            {p.services_offered.map((s) => (
              <span
                key={s}
                className="pill"
                style={{ background: "#F9FAFB", color: "var(--text-primary)", border: "1px solid var(--border-stone)" }}
              >
                {s}
              </span>
            ))}
          </div>
        </div>
      )}
      <ContactBlock item={p} />
      {p.bio && <BioBlock text={p.bio} />}
      <div className="mt-3 text-[10.5px] italic" style={{ color: "var(--text-secondary)" }}>
        Not an attorney — does not provide legal advice.
      </div>
    </div>
  );
}

function ContactBlock({ item }) {
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-4 text-xs" style={{ color: "var(--text-primary)" }}>
      <PlainField label="Email"      value={item.email}          icon={Mail}          full />
      <PlainField label="Phone"      value={item.phone}          icon={Phone} />
      {item.text_number && (
        <PlainField label="Text"     value={item.text_number}    icon={MessageSquare} />
      )}
      {item.website && (
        <PlainField label="Website"  value={item.website}        icon={Globe}         full />
      )}
      <PlainField
        label="Address"
        value={`${item.address}, ${item.city}, ${item.state} ${item.zip}`}
        icon={MapPin}
        full
      />
      {item.languages && item.languages.length > 0 && (
        <PlainField label="Languages" value={item.languages.join(", ")} icon={Languages} full />
      )}
    </div>
  );
}

function PlainField({ label, value, icon: Icon, full }) {
  if (!value) return null;
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10.5px] uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
        {label}
      </div>
      <div className="text-[13px] mt-0.5 flex items-start gap-1.5 select-text break-words">
        {Icon && <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: "var(--text-secondary)" }} />}
        {/* Deliberately NOT wrapping in <a> — plain text only. */}
        <span>{value}</span>
      </div>
    </div>
  );
}

function BioBlock({ text }) {
  return (
    <div className="mt-3 pt-3 border-t text-xs leading-relaxed"
         style={{ borderColor: "var(--border-stone)", color: "var(--text-primary)" }}>
      {text}
    </div>
  );
}

function FooterDisclaimer() {
  return (
    <div className="mt-8 text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>
      <div className="flex items-start gap-2">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <p>
          <strong>Are you a licensed attorney or a form-filling paralegal?</strong>{" "}
          You may <Link to="/directory/apply" data-testid="footer-apply-link" style={{ color: "var(--action)", fontWeight: 600 }}>request to be added to this directory</Link> for free.
          We do not charge for listings. Applications are manually reviewed but we do not vet claims.
          <ArrowRight className="w-3 h-3 inline ml-1" />
        </p>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
}
